// src/oura/screens/ContractTab.tsx
//
// The contract, as a contract (ourteam-frontend#3714, `T-5`), and the plan
// staff keep from it (plan table spec v1.1, 29 Sep 2026).
//
// One read of the engagement feeds every card: What was sold (with the start
// date, the one place it is set), where it departs from the catalogue, the
// documents, and The plan. The plan card is the list only; this screen owns
// the draft, the line drawer and the bar that saves it. A save sends the
// whole plan with a one-line summary, in the record's order, exactly as the
// PATCH has always taken it; the client's side reads the same record.
//
// DEVIATIONS ARE STAFF-ONLY. P0-5 took them out of the merchant payload, and
// this is the screen where they belong: the one place where the reason a number
// departs from the catalogue is useful, because it is the place someone has to
// defend it.

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarIcon, FileIcon, FileSpreadsheetIcon, FileTextIcon, FolderOpenIcon, GitBranchIcon, Loader2Icon, ScrollTextIcon, type LucideIcon } from "lucide-react";
import { useSnackbar } from "@hook/use-snackbar";
import {
    Dot,
    Drawer,
    FoldCard,
    FoldScope,
    IconSquare,
    ListRow,
    PillButton,
    Surface,
    TYPE,
    Tag,
    dataSel,
    drawerNav,
    drawerSequence,
    returnFocusChain,
    useEscapeLayer,
    useFoldOpener,
    type Tone,
} from "@oura/ui";
import { cn } from "../lib/cn";
import { joinDotted, shortDateOrNull } from "../lib/oura";
import { PlanCard } from "../components/PlanEditor";
import { PlanLineSheet, type LinePatch } from "../components/plan/PlanLineSheet";
import { todayIso } from "../components/plan/planDates";
import { PlanSaveBar, type SaveBarError } from "../components/plan/PlanSaveBar";
import { OutletNames } from "../components/plan/OutletNames";
import { PriorityKeywords } from "../components/plan/PriorityKeywords";
import { SignatureQuestions } from "../components/plan/SignatureQuestions";
import {
    buildPlanView,
    changeCount,
    cleanKeywords,
    coverCounts,
    formatMonths,
    groupId,
    isInvalid,
    isNotStarted,
    KEYWORDS_FIELD_ID,
    keywordsOf,
    outletNamesShown,
    OUTLET_NAMES_FIELD_ID,
    outletNameRows,
    outletNamesProblem,
    outletNamesToSend,
    outletDetailRows,
    outletDetailsChanged,
    outletEntriesToSend,
    resizeOutletDetails,
    type OutletDetail,
    OUTLETS_MAX,
    resizeOutletNames,
    lineFromDeliverable,
    nextXCode,
    pageGap,
    parseMonths,
    parseRefusal,
    place,
    placementMonths,
    sameSignature,
    SIGNATURE_FIELD_ID,
    signatureOf,
    toDeliverable,
    trackingWithKeywords,
    trackingWithSignature,
    type SignatureDraft,
    warningLines,
    xHighWater,
    xNumber,
    type DraftLine,
    type PlanChecks,
    type TimeGroup,
    type ViewGroup,
} from "../components/plan/planModel";
import { showOnboarding } from "../nav";
import { getEngagement, monthOf, updateEngagement, type Engagement, type EngagementDocument } from "@service/aeo/clientReview";

const SCOPE = "staff-contract";

type Load = { status: "loading" } | { status: "ok"; engagement: Engagement } | { status: "none" } | { status: "error" };

/** The HTTP status of a failed call, when it has one. */
function statusOf(err: unknown): number | null {
    return (err as { response?: { status?: number } } | null)?.response?.status ?? null;
}

export function ContractTab({ businessId, staffName, checks }: { businessId: string; staffName: string; checks?: PlanChecks }) {
    const [load, setLoad] = useState<Load>({ status: "loading" });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoad({ status: "loading" });
        getEngagement(businessId).then(
            (e) => {
                // Null is a real answer: a business exists before anything is sold (#3711 AC1).
                if (!cancelled) setLoad(e ? { status: "ok", engagement: e } : { status: "none" });
            },
            (err) => {
                // A 404 is the same answer on the live path. Anything else is a
                // failed read, never "nothing has been sold".
                if (!cancelled) setLoad(statusOf(err) === 404 ? { status: "none" } : { status: "error" });
            },
        );
        return () => {
            cancelled = true;
        };
    }, [businessId, attempt]);

    if (load.status === "loading") {
        return (
            <div className="flex justify-center py-24">
                <Loader2Icon aria-hidden className="h-6 w-6 animate-spin text-gray-400" />
                <span className="sr-only" role="status">
                    Loading the contract
                </span>
            </div>
        );
    }

    if (load.status === "none") {
        return (
            <Surface>
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>
                    {showOnboarding
                        ? "Nothing has been sold to this business yet. A contract appears once a signed quotation is activated. Start it from the banner at the top of the Client tab."
                        : "Nothing has been sold to this business yet. A contract appears once a signed quotation is activated. Open More on the Client tab, then choose Start a plan."}
                </p>
            </Surface>
        );
    }

    if (load.status === "error") {
        return (
            <div role="alert" className="flex flex-wrap items-center gap-3 rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200">
                <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1")}>Could not read the contract.</p>
                <PillButton size="sm" onClick={() => setAttempt((n) => n + 1)}>
                    Try again
                </PillButton>
            </div>
        );
    }

    return (
        <FoldScope value={SCOPE}>
            <ContractBody
                key={businessId}
                engagement={load.engagement}
                businessId={businessId}
                staffName={staffName}
                checks={checks}
                onSaved={(engagement) => setLoad({ status: "ok", engagement })}
            />
        </FoldScope>
    );
}

/* ------------------------------------------------------------------------- *
 * The draft
 * ------------------------------------------------------------------------- */

interface PlanDraft {
    /** Record order; new lines appended. */
    lines: DraftLine[];
    /** Saved lines taken out of the plan. */
    removed: string[];
    /** YYYY-MM-DD, or "" for none. */
    startDraft: string;
    /** The contract's priority keywords, as they are being edited (#3975). */
    keywords: string[];
    /** The contract's two signature questions, as they are being edited (ourteam-backend#5333). */
    signature: SignatureDraft;
    /** One name per outlet the contract sells, in contract order (#3985). */
    outletNames: string[];
    /** Each outlet's address and phone as on the quote, in the same order (#4776). */
    outletDetails: OutletDetail[];
    /** How many outlets the plan covers, as it is being edited (#4610). */
    outletCount: number;
    /** The highest custom number seen this session, so a removed X2 is never reissued. */
    xHigh: number;
    /** Skip public holidays in the plan's dates. Null while the server does not serve the setting. */
    skipHolidays: boolean | null;
}

function draftFrom(e: Engagement): PlanDraft {
    return {
        lines: e.deliverables.map((d, i) => lineFromDeliverable(d, `s${i}`)),
        removed: [],
        startDraft: (e.startedAt ?? "").slice(0, 10),
        keywords: keywordsOf(e),
        signature: signatureOf(e),
        outletNames: outletNameRows(e),
        outletDetails: outletDetailRows(e),
        outletCount: Math.max(1, e.outlets ?? 1),
        xHigh: xHighWater(e.deliverables.map((d) => d.code)),
        skipHolidays: typeof e.skipPublicHolidays === "boolean" ? e.skipPublicHolidays : null,
    };
}

/**
 * Drafts kept when the screen goes, by business. Moving to Timeline, another
 * tab or another business unmounts this screen, and an unsaved draft used to
 * go with it, unasked; now it is here when staff come back, with the save bar.
 * A reload or a closed tab still asks first (beforeunload). A kept draft is
 * used only on the record it was made from: once someone else has saved the
 * plan, it is dropped rather than laid over their change.
 */
interface KeptDraft {
    fingerprint: string;
    draft: PlanDraft;
    summary: string;
}
const keptDrafts = new Map<string, KeptDraft>();

function fingerprintOf(e: Engagement): string {
    return JSON.stringify([e.deliverables, e.startedAt, e.termMonths, e.pageUnits, keywordsOf(e), signatureOf(e), e.outletNames ?? null, e.skipPublicHolidays ?? null, e.changeLog.length]);
}

/** The kept draft for this business and this record, if there is one. */
function keptFor(businessId: string, e: Engagement): KeptDraft | null {
    const k = keptDrafts.get(businessId);
    if (!k) return null;
    if (k.fingerprint === fingerprintOf(e)) return k;
    keptDrafts.delete(businessId);
    return null;
}

/** New lines' ids, unique across visits, so a kept "new1" never meets a fresh one. */
let newLineSeq = 0;

/** The plan as it is being edited, rebuilt whenever a new record arrives (a save). */
function usePlanDraft(e: Engagement, kept: KeptDraft | null) {
    const [held, setHeld] = useState(() => ({ source: e, draft: kept?.draft ?? draftFrom(e) }));
    let draft = held.draft;
    if (held.source !== e) {
        draft = draftFrom(e);
        setHeld({ source: e, draft });
    }
    const update = useCallback((fn: (d: PlanDraft) => PlanDraft) => setHeld((h) => ({ ...h, draft: fn(h.draft) })), []);
    const savedStart = (e.startedAt ?? "").slice(0, 10);
    const savedKeywords = keywordsOf(e);
    // The keywords are one change however many phrases moved, as the start date is.
    const keywordsChanged = JSON.stringify(cleanKeywords(draft.keywords)) !== JSON.stringify(savedKeywords);
    // The names a saved plan holds: what the field compares against, and what
    // the server would still accept if nothing is touched.
    const savedOutletNames = outletNameRows({ outlets: e.outlets, outletNames: e.outletNames, outletSuggestions: [] });
    // The count is its own change; a new count always sends the names with it (ourteam-backend#6407).
    const outletCountChanged = draft.outletCount !== Math.max(1, e.outlets ?? 1);
    const outletNamesChanged =
        outletCountChanged || JSON.stringify(outletNamesToSend(draft.outletNames)) !== JSON.stringify(outletNamesToSend(savedOutletNames));
    // The quote's addresses and phones travel with the names, so a change to one sends them all (#4776).
    const savedOutletDetails = outletDetailRows(e);
    const outletDetailsMoved = outletDetailsChanged(draft.outletDetails, savedOutletDetails);
    const savedSignature = signatureOf(e);
    // Both questions are one change, as the keywords are.
    const signatureChanged = !sameSignature(draft.signature, savedSignature);
    const savedSkipHolidays = typeof e.skipPublicHolidays === "boolean" ? e.skipPublicHolidays : null;
    const skipHolidaysChanged = draft.skipHolidays !== null && draft.skipHolidays !== savedSkipHolidays;
    const count =
        changeCount(draft.lines, draft.removed.length, e.termMonths, draft.startDraft !== savedStart) +
        (keywordsChanged ? 1 : 0) +
        (signatureChanged ? 1 : 0) +
        (outletNamesChanged ? 1 : 0) +
        (outletDetailsMoved ? 1 : 0) +
        (outletCountChanged ? 1 : 0) +
        (skipHolidaysChanged ? 1 : 0);
    return {
        draft,
        count,
        savedStart,
        savedKeywords,
        keywordsChanged,
        setKeywords: (next: string[]) => update((d) => ({ ...d, keywords: next })),
        savedSignature,
        signatureChanged,
        setSignature: (next: SignatureDraft) => update((d) => ({ ...d, signature: next })),
        savedOutletNames,
        outletNamesChanged,
        outletCountChanged,
        savedOutletDetails,
        outletDetailsMoved,
        setOutletNames: (next: string[]) =>
            update((d) => ({
                ...d,
                outletNames: next,
                outletCount: Math.max(1, next.length),
                outletDetails: resizeOutletDetails(d.outletDetails, Math.max(1, next.length)),
            })),
        setOutletDetails: (next: OutletDetail[]) => update((d) => ({ ...d, outletDetails: next })),
        setOutletCount: (next: number) =>
            update((d) => ({
                ...d,
                outletCount: next,
                outletNames: resizeOutletNames(d.outletNames, next, e.outletSuggestions ?? []),
                outletDetails: resizeOutletDetails(d.outletDetails, next),
            })),
        setLine: (rid: string, patch: LinePatch) => update((d) => ({ ...d, lines: d.lines.map((l) => (l.rid === rid ? { ...l, ...patch } : l)) })),
        addLine: (line: DraftLine) => update((d) => ({ ...d, lines: [...d.lines, line], xHigh: Math.max(d.xHigh, xNumber(line.code) ?? 0) })),
        removeLine: (rid: string) =>
            update((d) => {
                const gone = d.lines.find((l) => l.rid === rid);
                return { ...d, lines: d.lines.filter((l) => l.rid !== rid), removed: gone?.saved ? [...d.removed, rid] : d.removed };
            }),
        setStart: (value: string) => update((d) => ({ ...d, startDraft: value })),
        savedSkipHolidays,
        skipHolidaysChanged,
        setSkipHolidays: (next: boolean) => update((d) => ({ ...d, skipHolidays: next })),
        discard: () => setHeld({ source: e, draft: draftFrom(e) }),
    };
}

/** Months as typed, tidied when they read: "2,3,4,5" -> "2-5". */
function tidyMonths(line: DraftLine, term: number): DraftLine {
    const p = parseMonths(line.monthsText, term);
    return p.ok ? { ...line, monthsText: formatMonths(p.months) } : line;
}

const FALLBACK_TITLE: Record<TimeGroup, string> = {
    none: "No month set",
    foundation: "Month 1 · Foundation",
    monthly: "Every month",
    milestones: "Milestones",
};

/**
 * Focus a plan row once it can take focus: a group opening renders it a
 * frame late, and a card unfolding keeps it inert until it has. Tried until
 * focus lands, for about half a second.
 */
function focusRowSoon(rid: string) {
    let tries = 0;
    const tick = () => {
        const el = document.querySelector<HTMLElement>(dataSel("plan-line", rid));
        if (el) {
            el.focus();
            if (document.activeElement === el) return;
        }
        tries += 1;
        if (tries < 30) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
}

/** The plan card's title (FoldCard anchor "the-plan"): focus goes back to it after a save. */
const CARD_TITLE = "#the-plan h2 button";

/** The plan card is folded right now: its rows are inert, so nothing in it can be shown or focused. */
function cardFolded(): boolean {
    return document.querySelector(CARD_TITLE)?.getAttribute("aria-expanded") === "false";
}

/* ------------------------------------------------------------------------- *
 * The screen
 * ------------------------------------------------------------------------- */

interface OpenLine {
    rid: string;
    /** The time group the row was opened from. */
    group: TimeGroup;
    /** That group's rows, in on-screen order, when it was opened. */
    ids: string[];
}

function ContractBody({
    engagement: e,
    businessId,
    staffName,
    checks,
    onSaved,
}: {
    engagement: Engagement;
    businessId: string;
    staffName: string;
    checks?: PlanChecks;
    onSaved: (e: Engagement) => void;
}) {
    const snackbar = useSnackbar();
    const openFold = useFoldOpener(SCOPE);
    const term = e.termMonths;
    // A publish conflict sends staff straight to the field that clears it
    // (#3975): `?focus=<field id>`, which unfolds its card for this visit only
    // and puts the cursor in it. Cleared from the URL afterwards, so a reload
    // is not another jump, and `persist: false` keeps it out of the stored
    // folds — a deep link is not a fold someone chose.
    const [params, setParams] = useSearchParams();
    const focusField = params.get("focus");
    useEffect(() => {
        if (focusField !== KEYWORDS_FIELD_ID && focusField !== SIGNATURE_FIELD_ID) return;
        openFold("sold", { persist: false });
        // The URL is cleared from inside the timer, after the focus. Clearing
        // it first changed `focusField`, which ran this effect's cleanup and
        // cancelled the timer before it fired: the card unfolded, the cursor
        // never arrived.
        const t = window.setTimeout(() => {
            const field = document.getElementById(focusField);
            field?.scrollIntoView({ block: "center", behavior: "smooth" });
            field?.focus();
            setParams(
                (p) => {
                    const next = new URLSearchParams(p);
                    next.delete("focus");
                    return next;
                },
                { replace: true },
            );
        }, 350);
        return () => window.clearTimeout(t);
    }, [focusField, openFold, setParams]);
    // Read once, on arrival: a draft left here before a move to another tab.
    const [kept] = useState(() => keptFor(businessId, e));
    const plan = usePlanDraft(e, kept);
    const { draft, count } = plan;

    const notStarted = isNotStarted(draft.startDraft, todayIso());
    // THE shared definition (#3748), on the date as it is being typed.
    const month = monthOf({ startedAt: draft.startDraft || null, termMonths: term });
    const groups = useMemo(() => buildPlanView(draft.lines, { term, month, notStarted }), [draft.lines, term, month, notStarted]);
    const groupOf = useMemo(() => {
        const m = new Map<string, ViewGroup>();
        for (const g of groups) for (const r of g.rows) m.set(r.line.rid, g);
        return m;
    }, [groups]);
    const lineById = useMemo(() => new Map(draft.lines.map((l) => [l.rid, l])), [draft.lines]);

    const [open, setOpen] = useState<OpenLine | null>(null);
    const [adding, setAdding] = useState<DraftLine | null>(null);
    const [summary, setSummary] = useState(() => kept?.summary ?? "");
    const summaryRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [barError, setBarError] = useState<SaveBarError | null>(null);
    const [refused, setRefused] = useState<Record<string, string[]>>({});
    const [revealAll, setRevealAll] = useState(false);
    const [warnings, setWarnings] = useState<string[]>([]);
    const refusedSet = useMemo(() => new Set(Object.keys(refused)), [refused]);
    // Lines that cannot be sent as they stand, as they stand now: "Fix 1 line
    // first" follows the fixes, and goes once the last one is fixed.
    const badLines = useMemo(() => draft.lines.filter((l) => isInvalid(l, term)), [draft.lines, term]);
    const shownError: SaveBarError | null =
        barError?.kind === "lines"
            ? badLines.length > 0
                ? { kind: "lines", count: badLines.length, label: badLines[0].label.trim() || badLines[0].code }
                : null
            : barError;
    // The page sum the server refuses on, said before Save rather than after.
    const gap = useMemo(() => pageGap(draft.lines, term, e.pageUnits), [draft.lines, term, e.pageUnits]);

    // Keep the draft for this business while it holds changes, so a move to
    // another tab does not lose it; drop it once there is nothing to keep.
    const fingerprint = useMemo(() => fingerprintOf(e), [e]);
    useEffect(() => {
        if (count > 0) keptDrafts.set(businessId, { fingerprint, draft, summary });
        else keptDrafts.delete(businessId);
    }, [businessId, fingerprint, draft, summary, count]);

    // Groups to open once the render that holds them has happened: a group
    // that only now exists registers with the opener on mount.
    const [toReveal, setToReveal] = useState<string[]>([]);
    useEffect(() => {
        if (toReveal.length === 0) return;
        toReveal.forEach((id) => openFold(id, { persist: false }));
        setToReveal([]);
    }, [toReveal, openFold]);
    const reveal = (ids: Array<string | null | undefined>) => {
        const list = ids.filter((x): x is string => Boolean(x));
        if (list.length === 0) return;
        // A group opens inside its card. "Add a line" sits on the card's head
        // and shows while it is folded, and a folded card keeps its rows inert:
        // open the card too, or the new or refused row is out of sight and
        // cannot take focus.
        if (cardFolded()) openFold("plan", { persist: false });
        setToReveal((cur) => Array.from(new Set([...cur, ...list])));
    };

    // Where focus goes when the drawer closes: set just before it closes.
    const returnTo = useRef<Array<string | null | undefined | false>>([]);
    // Nothing set (a new line cancelled): the shell goes back to what opened it.
    const returnFocus = useCallback(() => (returnTo.current.length > 0 ? returnFocusChain(...returnTo.current, CARD_TITLE)() : null), []);

    // A tab closed with changes still in the draft asks first.
    const dirty = count > 0;
    useEffect(() => {
        if (!dirty) return;
        const onUnload = (ev: BeforeUnloadEvent) => {
            ev.preventDefault();
            ev.returnValue = "";
        };
        window.addEventListener("beforeunload", onUnload);
        return () => window.removeEventListener("beforeunload", onUnload);
    }, [dirty]);

    const groupTitle = (key: TimeGroup) => groups.find((g) => g.key === key)?.title ?? FALLBACK_TITLE[key];

    /* Opening, moving, closing */

    const openLine = (rid: string, group: TimeGroup) => {
        // While a save runs, the draft is about to be replaced by what comes back.
        if (busy) return;
        const g = groups.find((x) => x.key === group);
        setAdding(null);
        setOpen({ rid, group, ids: g ? g.rows.map((r) => r.line.rid) : [rid] });
    };

    /** The line left the group it was opened from (its months changed): open where it went. */
    const revealIfMoved = (rid: string, from: TimeGroup) => {
        const now = groupOf.get(rid);
        if (now && now.key !== from) reveal([now.id]);
        return now;
    };

    const closeLine = () => {
        if (!open) return;
        const now = revealIfMoved(open.rid, open.group);
        returnTo.current = [dataSel("plan-line", open.rid), now && dataSel("group-head", now.id), dataSel("group-head", groupId(open.group))];
        if (now && now.key !== open.group) focusRowSoon(open.rid);
        setOpen(null);
    };

    const moveTo = (rid: string) => {
        if (!open) return;
        revealIfMoved(open.rid, open.group);
        setOpen({ ...open, rid });
    };

    const removeOpenLine = () => {
        if (!open) return;
        const g = groupOf.get(open.rid);
        const at = g ? g.rows.findIndex((r) => r.line.rid === open.rid) : -1;
        const next = g && at >= 0 ? g.rows[at + 1] : undefined;
        returnTo.current = [next && dataSel("plan-line", next.line.rid), g && g.rows.length > 1 && dataSel("group-head", g.id)];
        plan.removeLine(open.rid);
        setOpen(null);
    };

    const startAdd = () => {
        if (busy) return;
        newLineSeq += 1;
        const code = nextXCode(Math.max(draft.xHigh, xHighWater(draft.lines.map((l) => l.code))));
        setOpen(null);
        setAdding({
            rid: `new${newLineSeq}`,
            saved: null,
            code,
            label: "",
            monthsText: "",
            quantityText: "",
            // A custom line has no catalogue meaning: no scan check, confirmed
            // by hand, sent as an explicit empty list (ourteam-backend#4814).
            covers: [],
            reported_by_hand: true,
            coversOnRecord: true,
            handOnRecord: true,
            cadence: null,
            note: null,
        });
    };

    const confirmAdd = () => {
        if (!adding) return;
        const line = tidyMonths(adding, term);
        const { time } = place(line.code, placementMonths(line, term));
        plan.addLine(line);
        setAdding(null);
        reveal([groupId(time)]);
        returnTo.current = [dataSel("plan-line", line.rid), dataSel("group-head", groupId(time))];
        focusRowSoon(line.rid);
    };

    const cancelAdd = () => {
        returnTo.current = [];
        setAdding(null);
    };

    /* Saving */

    const discard = () => {
        plan.discard();
        keptDrafts.delete(businessId);
        setSummary("");
        setBarError(null);
        setRefused({});
        setRevealAll(false);
        // The bar goes with the changes: focus lands on the card, not the page.
        requestAnimationFrame(() => document.querySelector<HTMLElement>(CARD_TITLE)?.focus());
    };

    const showLine = (rid: string | null | undefined) => {
        const g = rid ? groupOf.get(rid) : undefined;
        if (!rid || !g) return;
        reveal([g.id]);
        openLine(rid, g.key);
    };

    const save = async () => {
        if (busy) return;
        if (!summary.trim()) {
            setBarError({ kind: "summary" });
            summaryRef.current?.focus();
            return;
        }
        if (badLines.length > 0) {
            setRevealAll(true);
            setBarError({ kind: "lines", count: badLines.length, label: badLines[0].label.trim() || badLines[0].code });
            reveal(badLines.map((l) => groupOf.get(l.rid)?.id));
            return;
        }
        // The outlets' names, only when they were touched: a plan saved for
        // another reason must not start demanding them (#3985). Checked here in
        // the server's own words rather than spending a round trip on a 400.
        const outletsSent = plan.outletNamesChanged || plan.outletDetailsMoved;
        const outletProblem = outletsSent ? outletNamesProblem(draft.outletNames, draft.outletCount) : null;
        if (outletProblem) {
            setBarError({ kind: "refused", total: 1, general: [outletProblem] });
            openFold("sold", { persist: false });
            requestAnimationFrame(() => document.getElementById(`${OUTLET_NAMES_FIELD_ID}-0`)?.focus());
            return;
        }
        // Record order, new lines last: grouping is display only.
        const sent = draft.lines;
        setBusy(true);
        setBarError(null);
        setWarnings([]);
        try {
            const next = await updateEngagement(
                businessId,
                {
                    deliverables: sent.map((l) => toDeliverable(l, term)),
                    startedAt: draft.startDraft || null,
                    // Merged with the contract's own tracking, so the baseline
                    // and source_assessment_id beside it survive the edit.
                    // The signature is sent only when it was edited: an
                    // untouched one would add an empty `signature` to a
                    // contract that never had one.
                    tracking: plan.signatureChanged
                        ? trackingWithSignature(trackingWithKeywords(e, draft.keywords), draft.signature)
                        : trackingWithKeywords(e, draft.keywords),
                    // Plain strings in contract order, and ONLY when they moved:
                    // sending them is what confirms them, and the server stamps
                    // confirmed-at every time it is sent.
                    // An outlet whose quote address or phone changed goes as {name, address?, phone?} (#4776).
                    ...(outletsSent ? { outletNames: outletEntriesToSend(draft.outletNames, draft.outletDetails, plan.savedOutletDetails) } : {}),
                    // A new count travels with its names, in the same PATCH (ourteam-backend#6407).
                    ...(plan.outletCountChanged ? { outlets: draft.outletCount } : {}),
                    // Only when it moved, and only once the server serves it.
                    ...(plan.skipHolidaysChanged && draft.skipHolidays !== null ? { skipPublicHolidays: draft.skipHolidays } : {}),
                    // H27: this edit was made from THIS read. Someone else's
                    // save in between is refused rather than overwritten.
                    expectedUpdatedAt: e.updatedAt ?? null,
                },
                staffName,
                summary.trim(),
            );
            setBusy(false);
            setSummary("");
            setRefused({});
            setRevealAll(false);
            setWarnings(warningLines((next as Engagement & { warnings?: unknown }).warnings, (i) => next.deliverables[i]?.label ?? null));
            onSaved(next);
            snackbar.showSuccess("Plan saved", "The client is told when you publish the timeline.");
            requestAnimationFrame(() => document.querySelector<HTMLElement>(CARD_TITLE)?.focus());
        } catch (err) {
            setBusy(false);
            const res = (err as { response?: { status?: number; data?: { errors?: unknown; message?: unknown } } } | null)?.response;
            if (res?.status === 409) {
                // Someone saved while this draft was open. The draft is kept —
                // it is the work — and the bar says to reload before saving.
                setBarError({ kind: "refused", total: 1, general: [typeof res.data?.message === "string" && res.data.message.trim()
                    ? res.data.message
                    : "Someone changed this contract a moment ago. Reload the page, then make the change again."] });
            } else if (res?.status === 400) {
                const r = parseRefusal(res.data?.errors, res.data?.message, sent.map((l) => l.rid));
                setRefused(r.byRid);
                setBarError({ kind: "refused", total: r.total, general: r.general });
                reveal(Object.keys(r.byRid).map((rid) => groupOf.get(rid)?.id));
            } else {
                setBarError({ kind: "network" });
            }
        }
    };

    /* The drawer */

    const editing = open ? lineById.get(open.rid) ?? null : null;
    const sheetLine = adding ?? editing;
    const titleOf = (id: string) => {
        const l = lineById.get(id);
        if (!l || !open || groupOf.get(id)?.key !== open.group) return null;
        return l.label.trim() || "No label yet";
    };
    const seq = open && editing ? drawerSequence(open.ids, open.rid, titleOf) : null;
    const nav = seq && open ? drawerNav(seq, { group: groupTitle(open.group), onMove: moveTo, keys: true, disabled: busy }) : undefined;
    const sheetGroup = sheetLine ? place(sheetLine.code, placementMonths(sheetLine, term)).time : null;

    const covers = coverCounts(draft.lines);

    return (
        // Room under the plan for the save bar that floats there: it is sticky,
        // so it hangs over whatever is at the foot of the window, and the
        // keyword chips were the thing it covered (#3989).
        <div className={cn("space-y-5", dirty && "pb-28")}>
            <SoldCard
                engagement={e}
                start={draft.startDraft}
                savedStart={plan.savedStart}
                onStart={plan.setStart}
                keywords={draft.keywords}
                outletNames={draft.outletNames}
                savedOutletNames={plan.savedOutletNames}
                onOutletNames={plan.setOutletNames}
                outletDetails={draft.outletDetails}
                savedOutletDetails={plan.savedOutletDetails}
                onOutletDetails={plan.setOutletDetails}
                outletCount={draft.outletCount}
                onOutletCount={plan.setOutletCount}
                savedKeywords={plan.savedKeywords}
                onKeywords={plan.setKeywords}
                signature={draft.signature}
                savedSignature={plan.savedSignature}
                onSignature={plan.setSignature}
                skipHolidays={draft.skipHolidays}
                savedSkipHolidays={plan.savedSkipHolidays}
                onSkipHolidays={plan.setSkipHolidays}
                busy={busy}
            />
            {e.deviations.length > 0 && <DeviationsCard deviations={e.deviations} />}
            {e.documents.length > 0 && <DocumentsCard documents={e.documents} />}
            <PlanCard
                groups={groups}
                lineCount={draft.lines.length}
                counts={covers}
                term={term}
                month={month}
                notStarted={notStarted}
                start={draft.startDraft}
                changes={count}
                refused={refusedSet}
                warnings={warnings}
                onDismissWarnings={() => setWarnings([])}
                changeLog={e.changeLog}
                pageGap={gap}
                onOpenLine={openLine}
                onShowPageLine={() => showLine(gap?.rid)}
                onAdd={startAdd}
            />

            <Drawer
                open={Boolean(sheetLine)}
                onClose={adding ? cancelAdd : closeLine}
                width={520}
                nav={adding ? undefined : nav}
                itemKey={sheetLine?.rid}
                returnFocus={returnFocus}
            >
                {sheetLine && (
                    <PlanLineSheet
                        key={sheetLine.rid}
                        mode={adding ? "add" : "edit"}
                        line={sheetLine}
                        term={term}
                        month={month}
                        notStarted={notStarted}
                        start={draft.startDraft}
                        groupTitle={sheetGroup ? groupTitle(sheetGroup) : null}
                        checks={checks}
                        pageUnits={e.pageUnits}
                        refusal={refused[sheetLine.rid] ?? null}
                        revealErrors={revealAll || (!adding && isInvalid(sheetLine, term))}
                        busy={busy}
                        onChange={(patch) => {
                            if (adding) setAdding((a) => (a ? { ...a, ...patch } : a));
                            else plan.setLine(sheetLine.rid, patch);
                        }}
                        onDone={closeLine}
                        onAdd={confirmAdd}
                        onCancel={cancelAdd}
                        onRemove={removeOpenLine}
                    />
                )}
            </Drawer>

            {dirty && (
                <PlanSaveBar
                    count={count}
                    summary={summary}
                    onSummary={(t) => {
                        setSummary(t);
                        if (barError?.kind === "summary" && t.trim()) setBarError(null);
                    }}
                    busy={busy}
                    error={shownError}
                    onSave={() => void save()}
                    onDiscard={discard}
                    onShowLine={() => showLine(badLines[0]?.rid)}
                    summaryRef={summaryRef}
                />
            )}
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * What was sold, departures, documents
 * ------------------------------------------------------------------------- */

const EMPTY = "–";

function Fact({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
    return (
        <div className="min-w-0">
            <dt className={TYPE.lab}>{label}</dt>
            <dd className={cn(TYPE.tb, "m-0 mt-0.5 break-words text-gray-900")}>{children}</dd>
            {hint && <dd className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{hint}</dd>}
        </div>
    );
}

function SoldCard({
    engagement: e,
    start,
    savedStart,
    onStart,
    keywords,
    outletNames,
    savedOutletNames,
    onOutletNames,
    outletDetails,
    savedOutletDetails,
    onOutletDetails,
    outletCount,
    onOutletCount,
    savedKeywords,
    onKeywords,
    signature,
    savedSignature,
    onSignature,
    skipHolidays,
    savedSkipHolidays,
    onSkipHolidays,
    busy,
}: {
    engagement: Engagement;
    start: string;
    savedStart: string;
    onStart: (value: string) => void;
    keywords: readonly string[];
    outletNames: readonly string[];
    savedOutletNames: readonly string[];
    onOutletNames: (next: string[]) => void;
    outletDetails: readonly OutletDetail[];
    savedOutletDetails: readonly OutletDetail[];
    onOutletDetails: (next: OutletDetail[]) => void;
    outletCount: number;
    onOutletCount: (next: number) => void;
    savedKeywords: readonly string[];
    onKeywords: (next: string[]) => void;
    signature: SignatureDraft;
    savedSignature: SignatureDraft;
    onSignature: (next: SignatureDraft) => void;
    skipHolidays: boolean | null;
    savedSkipHolidays: boolean | null;
    onSkipHolidays: (next: boolean) => void;
    busy: boolean;
}) {
    return (
        <FoldCard
            id="sold"
            title="What was sold"
            icon={{ icon: ScrollTextIcon, area: "dates" }}
            right={e.ref ? <span className="tabular-nums">{e.ref}</span> : undefined}
            foldedFact={joinDotted(e.planName, `${e.termMonths} ${e.termMonths === 1 ? "month" : "months"}`)}
        >
            <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                <Fact label="Client">{e.client || EMPTY}</Fact>
                <Fact label="Plan">{e.planName || EMPTY}</Fact>
                <Fact label="Starts">
                    <StartDate value={start} saved={savedStart} onChange={onStart} />
                </Fact>
                <Fact label="Term">
                    <span className="tabular-nums">
                        {e.termMonths} {e.termMonths === 1 ? "month" : "months"}
                    </span>
                </Fact>
                {/* The contract's page units are the monthly pages (G1), as on the quotation;
                    month 1's pages are B1's own count on its line. */}
                <Fact label="Monthly pages" hint="across the term">
                    <span className="tabular-nums">{e.pageUnits}</span>
                </Fact>
                <Fact label="Outlets">
                    <OutletCount value={outletCount} saved={Math.max(1, e.outlets ?? 1)} onChange={onOutletCount} disabled={busy} />
                </Fact>
            </dl>
            {skipHolidays !== null && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                    <PublicHolidays value={skipHolidays} saved={savedSkipHolidays} onChange={onSkipHolidays} disabled={busy} />
                </div>
            )}
            {/* Its own row under the facts: a chip list needs the width, and it
                is the one fact here that is edited rather than read (#3975). */}
            <div className="mt-4 border-t border-gray-100 pt-4">
                <PriorityKeywords value={keywords} saved={savedKeywords} onChange={onKeywords} disabled={busy} />
            </div>
            {/* The other fact the schedule cannot be published without (ourteam-backend#5333). */}
            <div className="mt-4 border-t border-gray-100 pt-4">
                <SignatureQuestions value={signature} saved={savedSignature} onChange={onSignature} disabled={busy} />
            </div>
            {/* One outlet too (#4592): its ticks are never gated on the name, but a
                GBP Pack is named after it and the server refuses one without it. */}
            {outletNamesShown(e) && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                    <OutletNames
                        count={outletCount}
                        value={outletNames}
                        saved={savedOutletNames}
                        confirmedAt={e.outletNamesConfirmedAt ?? null}
                        confirmedBy={e.outletNamesConfirmedBy ?? null}
                        suggestions={e.outletSuggestions ?? []}
                        filed={e.outletAreasFiled ?? []}
                        onChange={onOutletNames}
                        details={outletDetails}
                        savedDetails={savedOutletDetails}
                        onDetails={onOutletDetails}
                        disabled={busy}
                    />
                </div>
            )}
        </FoldCard>
    );
}

/**
 * Whether the plan's dates skip Malaysian public holidays (Jack, 22 Oct 2026).
 * On is today's rule. The Execution Timeline build reads the same setting, so
 * the PDF and Oura's schedule show the same dates. Saved with the plan, like
 * the start date: nothing reaches the client until the timeline is published.
 */
function PublicHolidays({ value, saved, onChange, disabled }: { value: boolean; saved: boolean | null; onChange: (next: boolean) => void; disabled: boolean }) {
    const id = useId();
    const changed = saved !== null && value !== saved;
    return (
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
                <p id={`${id}-label`} className={cn(TYPE.lab, "m-0")}>Public holidays</p>
                <p className={cn(TYPE.tb, "m-0 mt-0.5 text-gray-900")}>
                    {value ? "Skipped. Dates land on working days only." : "Counted as working days. Only weekends are skipped."}
                </p>
                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                    {changed
                        ? "Saving moves the dates. The client is told only when you publish the timeline."
                        : "Malaysian public holidays. The Execution Timeline build uses the same setting."}
                </p>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={value}
                aria-labelledby={`${id}-label`}
                disabled={disabled}
                onClick={() => onChange(!value)}
                className={cn(
                    "relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
                    value ? "bg-blue-600" : "bg-gray-300",
                )}
            >
                <span className="sr-only">Skip public holidays</span>
                <span aria-hidden className={cn("inline-block h-5 w-5 rounded-full bg-white shadow transition-transform", value ? "translate-x-[22px]" : "translate-x-0.5")} />
            </button>
        </div>
    );
}

const DATE_FIELD = cn(
    TYPE.tb,
    "block h-11 w-full max-w-[12rem] rounded-xl border-0 bg-gray-50 px-3 text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9",
);

/**
 * The start date, set here and nowhere else. "Change" opens a date field in
 * place; what is picked goes into the plan's draft and is saved with it, and
 * "Month 2 of 12" on the plan follows it as it is picked. `startedAt` is null
 * until payment clears, and a term has no dates until it does.
 *
 * A saved date can be moved, never cleared: the PATCH ignores an empty
 * start, so a cleared field used to count as a change, save as "Plan saved"
 * and come back with the old date. The field is left to the browser while it
 * is typed in (a date field is empty mid-edit), an empty value never reaches
 * the draft, and leaving it empty puts the date back and says why.
 */
/**
 * How many outlets the plan covers (#4610), changed in place like the start
 * date. The draft follows each step at once, so the Outlet names rows below
 * grow and shrink with it; nothing is saved until the plan is.
 */
function OutletCount({ value, saved, onChange, disabled }: { value: number; saved: number; onChange: (value: number) => void; disabled: boolean }) {
    const [editing, setEditing] = useState(false);
    const fieldId = useId();
    useEscapeLayer(() => setEditing(false), editing);
    const changed = value !== saved;
    return editing ? (
        <span className="flex flex-wrap items-center gap-2">
            <label htmlFor={fieldId} className="sr-only">
                Outlets
            </label>
            <input
                id={fieldId}
                type="number"
                min={1}
                max={OUTLETS_MAX}
                step={1}
                autoFocus
                value={value}
                disabled={disabled}
                onChange={(ev) => {
                    const n = Math.trunc(Number(ev.target.value));
                    if (Number.isFinite(n) && n >= 1 && n <= OUTLETS_MAX) onChange(n);
                }}
                onKeyDown={(ev) => {
                    if (ev.key === "Enter") {
                        ev.preventDefault();
                        setEditing(false);
                    }
                }}
                className={cn(DATE_FIELD, "max-w-[6rem] tabular-nums")}
            />
            <PillButton variant="text" onClick={() => setEditing(false)}>
                Done
            </PillButton>
        </span>
    ) : (
        <span className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="tabular-nums">{value}</span>
            {changed && <span className={cn(TYPE.tc, "text-amber-700")}>Not saved</span>}
            <span aria-hidden className="text-gray-400">
                ·
            </span>
            <PillButton variant="text" aria-label={`Change the number of outlets, ${value}`} disabled={disabled} onClick={() => setEditing(true)}>
                Change
            </PillButton>
        </span>
    );
}

function StartDate({ value, saved, onChange }: { value: string; saved: string; onChange: (value: string) => void }) {
    const [editing, setEditing] = useState(false);
    const [noClear, setNoClear] = useState(false);
    const fieldId = useId();
    const hintId = useId();
    const wrapRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const wasEditing = useRef(false);
    useEscapeLayer(() => setEditing(false), editing);
    useEffect(() => {
        const find = (sel: string) => wrapRef.current?.querySelector<HTMLElement>(sel);
        if (editing && !wasEditing.current) find("input")?.focus();
        if (!editing && wasEditing.current) find("[data-start-change]")?.focus();
        wasEditing.current = editing;
    }, [editing]);
    // The draft moved from elsewhere (Discard): the open field follows it.
    useEffect(() => {
        const el = inputRef.current;
        if (el && document.activeElement !== el && el.value !== value) el.value = value;
    }, [value]);

    const shown = shortDateOrNull(value);
    const changed = value !== saved;
    const hint = noClear && (
        <span id={hintId} className={cn(TYPE.tc, "block basis-full text-gray-500")}>
            A saved start date can be moved, not cleared.
        </span>
    );
    return (
        <span ref={wrapRef} className="block">
            {editing ? (
                <span className="flex flex-wrap items-center gap-2">
                    <label htmlFor={fieldId} className="sr-only">
                        Start date
                    </label>
                    <input
                        ref={inputRef}
                        id={fieldId}
                        type="date"
                        defaultValue={value}
                        aria-describedby={noClear ? hintId : undefined}
                        onChange={(ev) => {
                            const next = ev.target.value;
                            if (next) {
                                setNoClear(false);
                                onChange(next);
                            } else if (!saved) {
                                // Nothing saved yet: clearing a date picked here is fine.
                                onChange("");
                            }
                        }}
                        onBlur={(ev) => {
                            if (!ev.target.value && saved) {
                                ev.target.value = value || saved;
                                setNoClear(true);
                            }
                        }}
                        onKeyDown={(ev) => {
                            if (ev.key === "Enter") {
                                ev.preventDefault();
                                setEditing(false);
                            }
                        }}
                        className={DATE_FIELD}
                    />
                    <PillButton variant="text" onClick={() => setEditing(false)}>
                        Done
                    </PillButton>
                    {hint}
                </span>
            ) : (
                <span className="flex flex-wrap items-baseline gap-x-1.5">
                    <span className="tabular-nums">{shown ?? "When payment clears"}</span>
                    {changed && <span className={cn(TYPE.tc, "text-amber-700")}>Not saved</span>}
                    <span aria-hidden className="text-gray-400">
                        ·
                    </span>
                    <PillButton
                        data-start-change=""
                        variant="text"
                        aria-label={shown ? `Change the start date, ${shown}` : "Set the start date"}
                        onClick={() => {
                            setNoClear(false);
                            setEditing(true);
                        }}
                    >
                        {shown ? "Change" : "Set date"}
                    </PillButton>
                    {hint}
                </span>
            )}
        </span>
    );
}

function DeviationsCard({ deviations }: { deviations: string[] }) {
    return (
        <FoldCard
            id="deviations"
            title="Where this departs from the catalogue"
            icon={{ icon: GitBranchIcon, area: "setup" }}
            // Who can see it is a property of the card, so it sits in the head.
            right={<Tag tone="gray">Staff only</Tag>}
            foldedFact={<span className="tabular-nums">{deviations.length}</span>}
        >
            <div>
                {deviations.map((d, i) => (
                    <p key={i} className={cn(TYPE.tb, "m-0 flex gap-3 border-t border-gray-100 py-2.5 text-gray-700 first:border-t-0 first:pt-0")}>
                        <Dot tone="gray" className="mt-1.5" />
                        <span className="min-w-0 break-words">{d}</span>
                    </p>
                ))}
            </div>
        </FoldCard>
    );
}

function DocumentsCard({ documents }: { documents: EngagementDocument[] }) {
    return (
        <FoldCard
            id="documents"
            title="Documents"
            icon={{ icon: FolderOpenIcon, area: "setup" }}
            right={<span className="tabular-nums">{documents.length}</span>}
        >
            <div>
                {documents.map((d, i) => {
                    const { tone, icon } = documentIcon(d);
                    return <ListRow key={i} lead="square" icon={<IconSquare tone={tone} icon={icon} />} title={documentTitle(d)} sub={documentMeta(d) || undefined} />;
                })}
            </div>
        </FoldCard>
    );
}

/** The file icon by type: PDF red, Sheet green, Doc or Word blue, a dated schedule orange, else grey. */
function documentIcon(d: EngagementDocument): { tone: Tone; icon: LucideIcon } {
    const file = (d.file ?? "").toLowerCase();
    const kind = (d.kind ?? "").toLowerCase();
    if (kind === "execution_timeline") return { tone: "orange", icon: CalendarIcon };
    if (/\.pdf$/.test(file) || kind === "quotation") return { tone: "red", icon: FileTextIcon };
    if (/\.(xlsx?|csv)$/.test(file)) return { tone: "green", icon: FileSpreadsheetIcon };
    if (/\.docx?$/.test(file)) return { tone: "blue", icon: FileTextIcon };
    return { tone: "gray", icon: FileIcon };
}

/**
 * A document's name. An activated engagement's documents carry no `title`,
 * only a `kind` ("execution_timeline"), and rendering `d.title` left two bare
 * bullets on The Lush Clinic. Same precedence as the backend's own `_reports`:
 * title, then the kind in words, then the file name, then "Document".
 */
function documentTitle(d: EngagementDocument): string {
    if (d.title?.trim()) return d.title.trim();
    if (d.kind?.trim()) {
        const words = d.kind.trim().replace(/[_-]+/g, " ");
        return words.charAt(0).toUpperCase() + words.slice(1);
    }
    const file = d.file?.split("/").pop()?.trim();
    return file || "Document";
}

/** Reference · version · issued date · file name, whichever the entry has. */
function documentMeta(d: EngagementDocument): string {
    const issued = d.issued && /^\d{4}-\d{2}-\d{2}/.test(d.issued) ? shortDateOrNull(d.issued.slice(0, 10)) : null;
    // The file only when the title did not already come from it.
    const file = d.title?.trim() || d.kind?.trim() ? d.file?.split("/").pop() : null;
    return joinDotted(d.ref, d.version, issued && `Issued ${issued}`, file);
}
